import type { CartDataFragment } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

export const useOrderValidation = ({
  wantedTime,
  dropoffLocationId,
  billingAccountId,
  cart,
}: CanSubmitOrderProps) => {
  const shouldAllowCheckout = useCheckoutValidationFeatureFlag();
  const shouldShowErrorNotice = useDropoffSelectionErrorHandlingFlag();

  // Wanted Time
  const hasWantedTime = Boolean(wantedTime);

  // Dropoff Locations
  const hasMultipleDropoffLocations =
    (cart?.restaurant?.availableDropOffLocations ?? []).length > 1;
  const isNotDelivery = cart?.orderType !== 'DELIVERY';
  const needsDropoffLocation = isNotDelivery && hasMultipleDropoffLocations;

  const isDropoffSelected =
    Boolean(dropoffLocationId) && dropoffLocationId !== 'placeholder';
  const hasDropoffSelectionIfNeeded = needsDropoffLocation
    ? isDropoffSelected
    : true;

  // Billing Account
  const hasBillingAccountSelected = billingAccountId !== 'ADD';

  // Submit Order Validations
  const canSubmitOrder =
    hasWantedTime && hasBillingAccountSelected && hasDropoffSelectionIfNeeded;
  const shouldBlockCheckoutOnPress = shouldAllowCheckout && !canSubmitOrder;
  const cannotSubmitOrderReason = useCannotSubmitOrderReason({
    hasWantedTime,
    hasDropoffSelectionIfNeeded,
    hasBillingAccountSelected,
    cart,
  });

  return {
    canSubmitOrder: shouldAllowCheckout || canSubmitOrder,
    shouldBlockCheckoutOnPress,
    cannotSubmitOrderReason,
    shouldShowErrorNotice,
  };
};

const useCannotSubmitOrderReason = ({
  hasWantedTime,
  hasDropoffSelectionIfNeeded,
  hasBillingAccountSelected,
  cart,
}: CannotSubmitOrderReasonProps) => {
  const { t } = useLocalizationContext();

  if (!hasWantedTime) {
    return t('checkout.cannot-submit.wanted-time');
  }

  if (!hasDropoffSelectionIfNeeded) {
    return cart?.restaurant?.isOutpost
      ? t('checkout.cannot-submit.drop-off')
      : t('checkout.cannot-submit.pickup');
  }

  if (!hasBillingAccountSelected) {
    return t('checkout.cannot-submit.billing-account');
  }

  return t('general.error');
};

const useCheckoutValidationFeatureFlag = () => {
  return useFeatureFlag('OPS-XP-2978-checkout-validation', {
    listenForChanges: true,
  });
};

const useDropoffSelectionErrorHandlingFlag = () => {
  return useFeatureFlag('OPS-OP-615-order-validation-cc-and-pickup', {
    listenForChanges: true,
  });
};

type CanSubmitOrderProps = {
  readonly wantedTime?: string | null;
  readonly dropoffLocationId?: string | null;
  readonly billingAccountId?: string | null;
  readonly cart?: Partial<CartDataFragment> | null;
};

type CannotSubmitOrderReasonProps = {
  readonly hasWantedTime: boolean;
  readonly hasDropoffSelectionIfNeeded: boolean;
  readonly hasBillingAccountSelected: boolean;
  readonly cart?: Partial<CartDataFragment> | null;
};
